/**
 * Scrollable month selector used by the calendar header.
 */

import * as React from "react";
import { cn } from "../../../../lib/utils";
import { Icon } from "../../../icon";
import type { ResolvedDateLocale } from "../../shared/types";

interface MonthPickerListProps {
	selectedMonth: number;
	locale: ResolvedDateLocale;
	onMonthSelect(month: number): void;
}

const getMonthLabel = (month: number, locale: string): string =>
	new Intl.DateTimeFormat(locale, {
		month: "long",
		timeZone: "UTC",
	}).format(new Date(Date.UTC(2025, month, 1)));

export const MonthPickerList: React.FC<MonthPickerListProps> = ({
	selectedMonth,
	locale,
	onMonthSelect,
}) => {
	const selectedRef = React.useRef<HTMLButtonElement>(null);

	React.useEffect(() => {
		selectedRef.current?.scrollIntoView?.({
			block: "center",
			behavior: "instant",
		});
	}, []);

	const months = React.useMemo(
		() => Array.from({ length: 12 }, (_, month) => month),
		[],
	);

	return (
		<div
			className="h-full min-h-[240px] overflow-y-auto py-2"
			role="listbox"
			aria-label="Select month"
		>
			{months.map((month) => {
				const isSelected = month === selectedMonth;
				return (
					<button
						key={month}
						ref={isSelected ? selectedRef : undefined}
						type="button"
						role="option"
						aria-selected={isSelected}
						onClick={() => onMonthSelect(month)}
						className={cn(
							"flex min-h-12 w-full items-center gap-4 px-6 text-left",
							"text-[length:var(--md-typescale-body-large-size,16px)]",
							"text-m3-on-surface transition-m3-fast-effects",
							"hover:bg-m3-on-surface/8 focus-visible:outline-2 focus-visible:outline-m3-primary",
							isSelected &&
								"bg-m3-secondary-container text-m3-on-secondary-container",
						)}
					>
						<span className="w-6" aria-hidden>
							{isSelected && <Icon name="check" className="text-[20px]" />}
						</span>
						{getMonthLabel(month, locale.locale)}
					</button>
				);
			})}
		</div>
	);
};

MonthPickerList.displayName = "MonthPickerList";
